import { OpenFeature } from '@openfeature/web-sdk';
import { StatsigClient } from '@statsig/js-client';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { logger } from '@/lib/utils/logger';
import { StatsigWebProvider } from './statsig-openfeature-provider';

const STATSIG_NETWORK_TIMEOUT_MS = 3_000;
const STATSIG_INIT_TIMEOUT_MS = 5_000;

let initialized = false;
let currentUser: FeatureFlagUser | null = null;

interface FeatureFlagUser {
  id: string;
  email: string;
  organizationId: string;
  workspaceId: string | null;
}

function getAppVersion(): string | undefined {
  return import.meta.env.SENTRY_RELEASE || undefined;
}

/**
 * Maps SENTRY_ENVIRONMENT to Statsig's standard environment tiers.
 * Statsig uses `development`, `staging`, `production` — we normalize using
 * prefix matching to handle cloud-specific suffixes like `staging-gcp`,
 * `staging-aws`, `prod-eu`, etc.
 */
function getStatsigTier(): 'development' | 'staging' | 'production' {
  const env = (import.meta.env.SENTRY_ENVIRONMENT ?? '').toLowerCase();
  if (env.startsWith('prod')) return 'production';
  if (env.startsWith('staging') || env.startsWith('stage')) return 'staging';
  return 'development';
}

function buildContext(user: FeatureFlagUser) {
  const appVersion = getAppVersion();
  return {
    targetingKey: user.id,
    email: user.email,
    organizationId: user.organizationId,
    ...(user.workspaceId ? { workspaceId: user.workspaceId } : {}),
    ...(appVersion ? { appVersion } : {}),
  };
}

export async function initializeFeatureFlags(user: FeatureFlagUser) {
  const clientKey = import.meta.env.STATSIG_CLIENT_KEY;

  if (!clientKey || initialized) {
    return;
  }

  const appVersion = getAppVersion();
  const statsigClient = new StatsigClient(
    clientKey,
    {
      userID: user.id,
      email: user.email,
      ...(appVersion ? { appVersion } : {}),
      custom: {
        organizationId: user.organizationId,
        ...(user.workspaceId ? { workspaceId: user.workspaceId } : {}),
      },
    },
    {
      environment: { tier: getStatsigTier() },
      // Session replay intentionally not added — Sentry already records sessions.
      // To enable Statsig session replay later: install @statsig/session-replay,
      // import StatsigSessionReplayPlugin, and add `new StatsigSessionReplayPlugin()` here.
      plugins: [new StatsigAutoCapturePlugin()],
      networkConfig: { networkTimeoutMs: STATSIG_NETWORK_TIMEOUT_MS },
    },
  );

  // Race the init against a hard timeout so a slow/down Statsig never blocks the app.
  // On timeout, Statsig falls back to cached values (or all-default if no cache).
  const timeout = new Promise<'timeout'>((resolve) =>
    setTimeout(() => resolve('timeout'), STATSIG_INIT_TIMEOUT_MS),
  );
  const result = await Promise.race([statsigClient.initializeAsync(), timeout]);
  if (result === 'timeout') {
    logger.warn(
      `Statsig init exceeded ${STATSIG_INIT_TIMEOUT_MS}ms — proceeding with cached/default flags`,
    );
  }

  await OpenFeature.setContext(buildContext(user));
  await OpenFeature.setProviderAndWait(new StatsigWebProvider(statsigClient));

  currentUser = user;
  initialized = true;
}

/**
 * Update the OpenFeature evaluation context when the user's workspace changes.
 * The provider's `onContextChange` propagates the change to the Statsig client,
 * so flag targeting reflects the new workspace immediately.
 */
export async function updateFeatureFlagWorkspace(workspaceId: string | null) {
  if (!initialized || !currentUser) return;
  if (currentUser.workspaceId === workspaceId) return;

  currentUser = { ...currentUser, workspaceId };
  await OpenFeature.setContext(buildContext(currentUser));
}
